import React, { useState, useEffect, useRef } from 'react';
import TStyles from '../styles/TestStyles.module.css';
import GenStyles from "../styles/GenStyles.module.css";
import avatarImg from '../images/ai_speaker_image_transparent.png';
import avatarThinking from "../images/ai_speaker_thinking.png";
import avatarListening from "../images/ai_speaker_listening.png";
import speechBubble from "../images/thought_bubble.png";
import AudioRecorder from "../AudioRecorder";

const SpeakingTestInterface = ({ state, questionTotalTime, questionTimeRemaining, question, handleAudioPlaybackStopped } ) => {
    //const [state, setState] = useState('thinking'); // 'thinking', 'speaking', 'recording'
    const [recordingTimeLeft, setRecordingTimeLeft] = useState(questionTimeRemaining);
    const [totalTestTime, setTotalTestTime] = useState(0);
    const questionTextRef = useRef();
    const [audioUrl, setAudioUrl] = useState(null);

    // Simulate total test time counter
    useEffect(() => {
        const interval = setInterval(() => {
            setTotalTestTime(prev => prev + 1);
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Simulate countdown when recording
    useEffect(() => {
        if (state === 'recording' && recordingTimeLeft > 0) {
            const timer = setTimeout(() => {
                setRecordingTimeLeft(prev => prev - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [state, recordingTimeLeft]);
    useEffect(() => {
        if(question && question['question_audio_url']) {
            const url = '/ali_api/ALIAudioStreamer.php?filename=' + question['question_audio_url'] + "&t=" + Date.now();
            setAudioUrl(url);
        }
    }, [question])
    //Start playing audio if an audio URL is Supplied
    useEffect(() => {
        if(audioUrl) {
            console.log(audioUrl);
            let player = document.getElementById('audio_player');

            player.src = audioUrl;
            player
                .play()
                .catch(err => {
                    console.warn("Audio play was interrupted (dev/StrictMode)");
                });


            // Function to call when playback has ended
            const handleEnded = () => {
                onQuestionAudioPlaybackEnded();
            };

            // Add event listener
            player.addEventListener('ended', handleEnded);

            //If there is question text, start smooth scroll to bottom
            if(questionTextRef.current) {
                smoothScrollToBottom(questionTextRef.current, 47); // Scroll to bottom in 5 seconds
            }

            // Cleanup
            return () => {
                player.removeEventListener('ended', handleEnded);
            };
        }
    }, [audioUrl])

    //Other Functions

    //For displaying total test time and question time remaining (if applicable)
    function formatSecondsToMinutesAndSeconds(totalSeconds) {
        // Calculate minutes and remaining seconds
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;

        // Format the minute part with proper singular/plural
        const minuteText = minutes === 1 ? "1 minute" : `${minutes} minutes`;

        // Format the second part with proper singular/plural
        const secondText = seconds === 1 ? "1 second" : `${seconds} seconds`;

        // Handle special case of zero minutes
        if (minutes === 0) {
            return secondText;
        }

        // Handle special case of zero seconds
        if (seconds === 0) {
            return minuteText;
        }

        // Combine minutes and seconds
        return `${minuteText} ${secondText}`;
    }



    const renderStateIndicator = () => {
        switch (state) {
            case 'thinking':
                return <div className={TStyles.thinking_bubble}>Thinking<span className={TStyles.ellipsis}>...</span></div>
            case 'recording':
                return <div className={TStyles.recording_bubble}>Recording<span className={TStyles.ellipsis}>...</span></div>
            case 'speaking':
                return <div className={TStyles.speaking_bubble}><span className={TStyles.ellipsis}>...</span>Speaking</div>
            default:
                return <></>;
        }
    }
    const renderWaveFormVisualizer = () => {
        switch (state) {
            case 'recording':
                return <div className={state === 'speaking' || state === 'recording' ? TStyles.waveform_active : TStyles.waveform_idle}>WAV</div>
            case 'speaking':
                return <div className={state === 'speaking' || state === 'recording' ? TStyles.waveform_active : TStyles.waveform_idle}>WAV</div>
            default:
                return <></>;
        }
    }

    //Makes scrolling of the question text start and end gradually
    function easeInOutQuad(t) {
        return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    }
    //If the question text is too large for the display box, scroll it
    function smoothScrollToBottom(element, duration) {
        const start = element.scrollTop;
        const end = element.scrollHeight - element.clientHeight;
        const distance = end - start;

        if (distance <= 0) return; // Already at bottom

        const startTime = performance.now();

        function step(currentTime) {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / (duration * 1000), 1); // duration in seconds -> milliseconds

            const easedProgress = easeInOutQuad(progress);

            element.scrollTop = start + distance * easedProgress;

            if (progress < 1) {
                requestAnimationFrame(step);
            }
        }

        requestAnimationFrame(step);
    }

    //Handles playback of the question audio (triggering recording start in the parent component)
    const onQuestionAudioPlaybackEnded = () => {
        console.log("Playback finished.");
        handleAudioPlaybackStopped();
    }

    //Format the question text... converting line breaks (\n) to paragraphs (<p></p>)
    const formatQuestionText = () => {
        if(!question || !question['question_text']) {
            return '';
        }

        return question['question_text'].split("\n").map((line, index) => (
            <p key={index}>{line.trim()}</p>
        ));
    }
    //Quick function to check whether question_text exists
    const isQuestionText = () => {
        return (question && question['question_text']);
    }




    return (
        <div className={TStyles.testInterface}>
            {/* State indicator (thought bubble) */}
            <div className={`${TStyles.state_indicator} ${state === 'thinking' ? TStyles.thinking_state : state === 'speaking' ? TStyles.speaking_state : TStyles.recording_state}`}>
                {renderStateIndicator()}
            </div>

            {/* AI Avatar */}
            <img src={state === 'speaking' ? avatarImg : state === 'recording' ? avatarListening : avatarThinking} alt="AI Avatar" className={TStyles.avatar_image} />

            {/* Waveform visualizer */}
            {/*<div className={TStyles.waveform_visualizer}>*/}
            {/*    /!*{renderWaveFormVisualizer()}*!/*/}
            {/*</div>*/}

            {/* Timers */}
            <div className={TStyles.timer_container}>

                    {/* Progress Bar Countdown for Recording Time Remaining */}

                    <div className={TStyles.progress_bar_wrapper}>
                        {state === 'recording' ? (
                            <div
                                className={TStyles.progress_bar_fill}
                                style={{ width: `${(questionTimeRemaining / questionTotalTime) * 100}%` }}
                            ></div>
                        ) : ''}
                    </div>

                    {/* Question Text */}
                    <div
                        className={
                            (isQuestionText() && state !== 'thinking') ?
                                TStyles.question_text :
                                GenStyles.hidden}
                        ref={questionTextRef}
                    >
                        {isQuestionText() ? formatQuestionText() : ''}
                    </div>


                    {/* Total Test Time */}
                    <div className={TStyles.total_timer}>Total Time: {formatSecondsToMinutesAndSeconds(totalTestTime)}</div>


            </div>

            <div>
                {audioUrl && (
                    <audio
                        className={TStyles.audio_interface_hidden}
                        controls
                        autoPlay
                        id="audio_player"
                        //onEnded={handle_ai_finished_talking}
                    >
                        <source src={audioUrl} type="audio/mpeg" />
                        Your browser does not support the audio tag.
                    </audio>
                )}
            </div>

        </div>
    )



};

export default SpeakingTestInterface;